/* ============================================
   SENSICA — Shared Components & Interactions
   ============================================ */

document.addEventListener('DOMContentLoaded', () => {
  initHeader();
  initMobileNav();
  initScrollReveal();
  initFAQ();
  initStickyCart();
  initHeroDrawers();
  initHeroGallery();
  initRegion();
});

/* ── Region Detection (UK/US-only elements) ── */
function initRegion() {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  const isUK = tz.startsWith('Europe/London') || tz.startsWith('Europe/Belfast');
  const isUS = tz.startsWith('America/');
  if (isUK) {
    document.querySelectorAll('[data-region="uk"]').forEach(el => {
      el.style.display = '';
    });
    document.querySelectorAll('[data-hide-region="uk"]').forEach(el => {
      el.style.display = 'none';
    });
  }
  if (isUS) {
    document.querySelectorAll('[data-region="us"]').forEach(el => {
      el.style.display = '';
    });
  }
}

/* ── Hero Gallery (Thumbnails + Carousel) ── */
function initHeroGallery() {
  document.querySelectorAll('.hero__gallery').forEach(gallery => {
    const slides = gallery.querySelectorAll('.hero__gallery-slide');
    const thumbs = gallery.querySelectorAll('.hero__gallery-thumb');
    const dots = gallery.querySelectorAll('.hero__gallery-dot');
    const main = gallery.querySelector('.hero__gallery-main');
    if (!slides.length) return;

    let current = 0;

    function goTo(index) {
      current = (index + slides.length) % slides.length;
      slides.forEach((s, i) => s.classList.toggle('active', i === current));
      thumbs.forEach((t, i) => t.classList.toggle('active', i === current));
      dots.forEach((d, i) => d.classList.toggle('active', i === current));
    }

    // Thumbnail clicks
    thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => goTo(i));
    });

    // Dot clicks
    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => goTo(i));
    });

    // Touch swipe (mobile)
    if (main) {
      let startX = 0;
      let startY = 0;
      let isDragging = false;

      main.addEventListener('touchstart', e => {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        isDragging = true;
      }, { passive: true });

      main.addEventListener('touchend', e => {
        if (!isDragging) return;
        isDragging = false;
        const dx = e.changedTouches[0].clientX - startX;
        const dy = e.changedTouches[0].clientY - startY;
        if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 40) {
          dx < 0 ? goTo(current + 1) : goTo(current - 1);
        }
      }, { passive: true });
    }
  });
}

/* ── Hero Drawers (Accordion) ────────────── */
function initHeroDrawers() {
  document.querySelectorAll('.hero__drawer-toggle').forEach(btn => {
    btn.addEventListener('click', () => {
      const drawer = btn.closest('.hero__drawer');
      drawer.classList.toggle('open');
    });
  });
}


/* ── Header & Navigation ──────────────────── */
function initHeader() {
  const header = document.querySelector('.header');
  if (!header) return;

  // Match the header to the section directly beneath it. A page can contain
  // several dark sections, so a document-wide query selects the wrong one.
  const updateHeaderTheme = () => {
    if (header.classList.contains('scrolled')) {
      header.removeAttribute('data-header-theme');
      return;
    }
    const headerStyle = header.getAttribute('data-header-style') || 'adaptive';
    if (headerStyle === 'frosted') {
      header.removeAttribute('data-header-theme');
      return;
    }
    const headerBottom = header.getBoundingClientRect().bottom;
    const sections = Array.from(document.querySelectorAll('main [data-header-theme]'));
    const activeSection = sections.find((section) => {
      const rect = section.getBoundingClientRect();
      return rect.top <= headerBottom + 1 && rect.bottom > headerBottom;
    });
    
    if (activeSection?.dataset.headerTheme === 'dark') {
      header.setAttribute('data-header-theme', 'dark');
    } else {
      header.removeAttribute('data-header-theme');
    }
  };

  updateHeaderTheme();

  let lastScroll = 0;
  window.addEventListener('scroll', () => {
    const y = window.scrollY;
    header.classList.toggle('scrolled', y > 20);

    updateHeaderTheme();

    lastScroll = y;
  }, { passive: true });
}


function initMobileNav() {
  const btn = document.querySelector('.header__hamburger');
  const nav = document.querySelector('.mobile-nav');
  const overlay = document.querySelector('.mobile-nav__overlay');
  if (!btn || !nav) return;

  const toggle = () => {
    const isOpen = nav.classList.toggle('open');
    overlay?.classList.toggle('open');
    btn.classList.toggle('open', isOpen);
    document.querySelector('.header')?.classList.toggle('menu-open', isOpen);
    btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    document.body.style.overflow = isOpen ? 'hidden' : '';
  };

  btn.addEventListener('click', toggle);
  overlay?.addEventListener('click', toggle);
  nav.querySelectorAll('a').forEach(a => a.addEventListener('click', toggle));
}

/* ── Scroll Reveal ────────────────────────── */
function initScrollReveal() {
  const elements = document.querySelectorAll('.reveal');
  if (!elements.length) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('visible');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.15, rootMargin: '0px 0px -40px 0px' });

  elements.forEach(el => observer.observe(el));
}

/* ── FAQ Accordion ────────────────────────── */
function initFAQ() {
  document.querySelectorAll('.faq-item__question').forEach(btn => {
    btn.addEventListener('click', () => {
      const item = btn.closest('.faq-item');
      const wasOpen = item.classList.contains('open');

      // Close all in same list
      item.closest('.faq-list')?.querySelectorAll('.faq-item').forEach(i => i.classList.remove('open'));

      if (!wasOpen) item.classList.add('open');
    });
  });
}

/* ── Sticky Cart Bar ──────────────────────── */
function initStickyCart() {
  const cart = document.querySelector('.sticky-cart');
  const hero = document.querySelector('.hero');
  if (!cart || !hero) return;

  const observer = new IntersectionObserver(([entry]) => {
    cart.classList.toggle('visible', !entry.isIntersecting);
  }, { threshold: 0 });

  observer.observe(hero);
}

/* ── Header HTML Injection ────────────────── */
function getHeaderHTML(activePage) {
  return `
    <header class="header">
      <div class="header__inner">
        <a href="/index.html" class="header__logo">SENSICA</a>
        <nav class="header__nav">
          <div class="nav-dropdown">
            <a href="#" class="${activePage === 'treatments' ? 'active' : ''}">Treatments ▾</a>
            <div class="nav-dropdown__menu">
              <a href="/treatments/hair-removal.html"><span class="dot" style="background:var(--hair)"></span> Hair Removal</a>
              <a href="/treatments/anti-aging.html"><span class="dot" style="background:var(--aging)"></span> Anti-Aging</a>
              <a href="/treatments/body-smoothing.html"><span class="dot" style="background:var(--body)"></span> Body Sculpting</a>
            </div>
          </div>
          <div class="nav-dropdown">
            <a href="#" class="${activePage === 'education' ? 'active' : ''}">Education ▾</a>
            <div class="nav-dropdown__menu">
              <a href="/education/reactive-ipl.html">Reactive IPL™</a>
              <a href="/education/rf-101.html">Dynamic RF™</a>
              <a href="/education/how-it-works.html">Lipotherm RF™</a>
              <a href="/education/blog.html">Blog: The Edit</a>
              <a href="#">Sensica App</a>
              <a href="/about.html">About</a>
            </div>
          </div>
          <a href="/quiz.html" class="${activePage === 'quiz' ? 'active' : ''}">Find your Treatment</a>
        </nav>
        <button class="header__hamburger" aria-label="Menu">
          <span></span><span></span><span></span>
        </button>
      </div>
    </header>
    <div class="mobile-nav__overlay"></div>
    <nav class="mobile-nav">
      <a href="/index.html">Home</a>
      <a href="/treatments/hair-removal.html">Hair Removal</a>
      <a href="/treatments/anti-aging.html">Anti-Aging</a>
      <a href="/treatments/body-smoothing.html">Body Sculpting</a>
      <div class="mobile-nav__divider"></div>
      <a href="/education/reactive-ipl.html">Reactive IPL™</a>
      <a href="/education/rf-101.html">Dynamic RF™</a>
      <a href="/education/how-it-works.html">Lipotherm RF™</a>
      <a href="/education/blog.html">Blog: The Edit</a>
      <a href="#">Sensica App</a>
      <div class="mobile-nav__divider"></div>
      <a href="/quiz.html">Find your Treatment</a>
    </nav>
  `;
}

/* ── Footer HTML Injection ────────────────── */
function getFooterHTML() {
  return `
    <footer class="footer">
      <div class="container">
        <div class="footer__grid">
          <div class="footer__brand">
            <div class="footer__logo">SENSICA</div>
            <p class="footer__tagline">Real beauty. Real stories. Real bond. Professional beauty technology for treatments you do at home — on your terms.</p>
          </div>
          <div>
            <div class="footer__col-title">Treatments</div>
            <div class="footer__links">
              <a href="/treatments/hair-removal.html">Hair Removal</a>
              <a href="/treatments/anti-aging.html">Anti-Aging</a>
              <a href="/treatments/body-smoothing.html">Body Sculpting</a>
            </div>
          </div>
          <div>
            <div class="footer__col-title">Products</div>
            <div class="footer__links">
              <a href="/treatments/sensilight-pro.html">Sensilight Pro</a>
              <a href="/treatments/sensilift.html">Sensilift</a>
              <a href="/treatments/sensifirm.html">Sensifirm</a>
              <a href="/treatments/sensismooth.html">Sensismooth</a>
            </div>
          </div>
          <div>
            <div class="footer__col-title">Company</div>
            <div class="footer__links">
              <a href="/about.html">About</a>
              <a href="/contact.html">Contact</a>
            </div>
          </div>
        </div>
        <div class="footer__bottom">
          <p>© 2026 Sensica. All rights reserved.</p>
          <div class="footer__social">
            <a href="#" aria-label="Instagram">📷</a>
            <a href="#" aria-label="Facebook">📘</a>
            <a href="#" aria-label="YouTube">▶️</a>
          </div>
        </div>
      </div>
    </footer>
  `;
}

/* ── Global Link Localization Fix ─────────── */
function localizeLinks() {
  const root = window.Shopify?.routes?.root;
  if (!root || root === '/') return;
  
  const cleanRoot = root.replace(/\/+$/, '');
  const rootPrefix = cleanRoot + '/';
  
  document.querySelectorAll('a').forEach(link => {
    let href = link.getAttribute('href');
    if (!href) return;
    if (href.startsWith('//')) return; // Ignore protocol-relative URLs
    if (href.startsWith('#')) return; // Ignore anchors
    if (href.startsWith('mailto:') || href.startsWith('tel:') || href.startsWith('javascript:')) return;
    
    // Convert absolute internal URLs to relative for checking
    if (href.startsWith('http')) {
      try {
        const urlObj = new URL(href);
        if (urlObj.hostname !== window.location.hostname) return;
        href = urlObj.pathname + urlObj.search + urlObj.hash;
      } catch(e) {
        return;
      }
    }
    
    // Ensure we are only touching root-relative paths
    if (!href.startsWith('/')) return;
    
    // Link already points to localized home (with or without trailing slash)
    if (href === '/' || href === cleanRoot || href === rootPrefix) {
      link.setAttribute('href', cleanRoot);
      return;
    }
    
    // Home link with query params or hash (e.g. /?ref=... or /es?ref=...)
    if (href.startsWith('/?') || href.startsWith('/#')) {
      link.setAttribute('href', cleanRoot + href);
      return;
    }
    if (href.startsWith(cleanRoot + '?') || href.startsWith(cleanRoot + '#')) {
      return;
    }
    
    // Link already localized to this market/language (e.g. /es/products/...)
    if (href.startsWith(rootPrefix)) {
      return;
    }
    
    // Prevent corrupting links that target a different language (e.g. /fr/, /de/, /it/)
    if (/^\/[a-z]{2}(?:-[a-zA-Z]{2})?(?:\/|\?|#|$)/i.test(href)) {
      return;
    }
    
    // Prepend locale prefix to unlocalized relative path (e.g. /pages/about -> /es/pages/about)
    link.setAttribute('href', rootPrefix + href.substring(1));
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', localizeLinks);
} else {
  localizeLinks();
}
